import React from 'react'
import PropTypes from 'prop-types'
import { Dropdown as SemanticDropdown } from 'semantic-ui-react'
import './WingsSelectInput.css'

/**
 * WingsSelectInput Component
 *
 * A searchable/selectable dropdown input styled to match the Wingspans input
 * system. Built on Semantic UI Dropdown for features like search and additions.
 */
const WingsSelectInput = ({
  label,
  helpText,
  error,
  id,
  options = [],
  value,
  onChange,
  onSearchChange,
  onAddItem,
  placeholder,
  noResultsMessage,
  fullWidth = false,
  disabled = false,
  allowAdditions = false,
  search = true,
  className = '',
  onBlur,
  ...props
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

  const wrapperClasses = [
    'wings-select-input-wrapper',
    fullWidth && 'wings-select-input-wrapper--full',
    error && 'wings-select-input-wrapper--error',
    className,
  ].filter(Boolean).join(' ')

  const dropdownClasses = [
    'wings-select-input',
    error && 'wings-select-input--error',
    disabled && 'wings-select-input--disabled',
  ].filter(Boolean).join(' ')

  const handleChange = (e, data) => {
    const nextValue = (data?.value ?? '').toString().trim()
    onChange?.(nextValue, data)
  }

  const handleSearchChange = (e, data) => {
    const query = (data?.searchQuery ?? '').toString()
    onSearchChange?.(query, data)
  }

  const handleAddItem = (e, data) => {
    const nextValue = (data?.value ?? '').toString().trim()
    onAddItem?.(nextValue, data)
  }

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="wings-select-input-label" htmlFor={inputId}>
          {label}
        </label>
      )}

      <SemanticDropdown
        id={inputId}
        className={dropdownClasses}
        selection
        search={search}
        fluid={fullWidth}
        allowAdditions={allowAdditions}
        options={options}
        value={value || undefined}
        placeholder={placeholder}
        noResultsMessage={noResultsMessage}
        disabled={disabled}
        onChange={handleChange}
        onSearchChange={handleSearchChange}
        onAddItem={handleAddItem}
        onBlur={onBlur}
        aria-describedby={helpText || error ? `${inputId}-hint` : undefined}
        aria-invalid={!!error}
        {...props}
      />

      {(helpText || error) && (
        <span
          id={`${inputId}-hint`}
          className={`wings-select-input-hint${error ? ' wings-select-input-hint--error' : ''}`}
        >
          {error || helpText}
        </span>
      )}
    </div>
  )
}

WingsSelectInput.propTypes = {
  /** Label displayed above the field */
  label: PropTypes.string,
  /** Helper text displayed below the field */
  helpText: PropTypes.string,
  /** Error message — puts the field in an error state and replaces helpText */
  error: PropTypes.string,
  /** HTML id for the field; auto-generated from label if omitted */
  id: PropTypes.string,
  /** Semantic UI dropdown options [{ key, text, value }] */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  /** Selected value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Called on change with (value, data) */
  onChange: PropTypes.func,
  /** Called on search change with (query, data) */
  onSearchChange: PropTypes.func,
  /** Called when user adds an item with (value, data) */
  onAddItem: PropTypes.func,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Message shown when no search results are found */
  noResultsMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Expands the field to fill its container width */
  fullWidth: PropTypes.bool,
  /** Disables the field */
  disabled: PropTypes.bool,
  /** Allows custom values to be added */
  allowAdditions: PropTypes.bool,
  /** Enables search input in the dropdown */
  search: PropTypes.bool,
  /** Additional CSS classes applied to the wrapper element */
  className: PropTypes.string,
  /** Blur handler */
  onBlur: PropTypes.func,
}

export default WingsSelectInput